import '../index.scss';
import { useEffect, useRef, FC } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormLabel,
  TextField,
} from '@material-ui/core';
import { Select } from '@sharedComponents/DesignSystem/Inputs';
import { translate } from '@utils';
import { SubmissionGroupEntityInterface, TranslationKey } from '@types';

interface SaveModalProps {
  open: boolean;
  value: string;
  contentName: TranslationKey;
  groupTemplateId?: string;
  submissionGroups?: SubmissionGroupEntityInterface[];
  isDisabledConfirmationButton: boolean;
  isGroupSelectDisable: boolean;
  isCurrentSubmissionTemplateEditorFormChanged?: boolean;
  isSampleSaving?: boolean;
  // TODO: Define all props
  [key: string]: any;
}

export const SaveModal: FC<SaveModalProps> = ({
  open,
  value,
  submissionGroups,
  modalTemplateType,
  submissionsTypes,
  groupTemplateId,
  contentName,
  handleCancel,
  handleConfirmation,
  handleOnTemplateTypeChange,
  handleOnGroupTemplateIdChange,
  onChange,
  isDisabledConfirmationButton,
  isGroupSelectDisable,
  isCurrentSubmissionTemplateEditorFormChanged,
  isSampleSaving,
}) => {
  const isTitleValid = Boolean(value.trim());
  const isSaveButtonDisabled =
    !isTitleValid || !groupTemplateId || !modalTemplateType || isDisabledConfirmationButton;
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) {
      inputRef.current.focus();
    }
  }, [open]);

  return (
    <Dialog className="save-modal" open={open} keepMounted onClose={handleCancel}>
      <DialogTitle data-testid="save-modal-title">
        {isSampleSaving ? translate('save_as_new_sample') : translate('save_template_as')}
      </DialogTitle>
      {isCurrentSubmissionTemplateEditorFormChanged && !isSampleSaving && (
        <DialogContentText
          className="save-modal-notify"
          data-testid="unsaved-template-changes-label"
        >
          {translate('unsaved_template_changes')}
        </DialogContentText>
      )}

      <DialogContent className="save-modal-content">
        <DialogContentText className="modal-text-field-title">
          {translate('template_input_name')}
        </DialogContentText>
        <TextField
          className="modal-text-field"
          value={value}
          onChange={onChange}
          placeholder={translate(contentName)}
          inputRef={inputRef}
          data-testid="modal-text-field-name"
        />
      </DialogContent>
      <DialogContent className="save-modal-content">
        <FormControl variant="standard">
          <FormLabel className="save-modal-content__where">
            {translate('save_template_where')}
          </FormLabel>
          <Select
            value={modalTemplateType}
            handleChange={handleOnTemplateTypeChange}
            options={submissionsTypes}
            placeholder={translate('choose')}
            data-testid="save-modal-content-where"
            disabled={isSampleSaving}
          />
        </FormControl>
      </DialogContent>
      <DialogContent className="save-modal-content">
        <FormControl variant="standard">
          <FormLabel className="save-modal-content__group">
            {translate('save_template_group')}
          </FormLabel>
          <Select
            disabled={isGroupSelectDisable}
            value={groupTemplateId}
            handleChange={handleOnGroupTemplateIdChange}
            options={submissionGroups.map((option) => ({ label: option.name, value: option.id }))}
            placeholder={translate('choose')}
            data-testid="save-modal-content-group"
          />
        </FormControl>
      </DialogContent>
      <DialogActions className="modal-actions">
        <Button
          disabled={isSaveButtonDisabled}
          onClick={handleConfirmation}
          className="actions-button save"
          disableRipple
          data-testid="save-button"
        >
          {translate('save')}
        </Button>
        <Button
          onClick={handleCancel}
          className="actions-button cancel"
          disableRipple
          data-testid="cancel-button"
        >
          {translate('cancel')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
